'use client';

import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { BackgroundVideo } from './BackgroundVideo';

type PublicErrorSectionProps = {
  children: ReactNode;
  ariaLabelledBy?: string;
};

/**
 * Full-bleed video backdrop for error/not-found states inside the public
 * search layout. Applies negative margins to escape the layout's inner
 * padding so the video fills edge-to-edge.
 */
export function PublicErrorSection({
  children,
  ariaLabelledBy,
}: PublicErrorSectionProps) {
  return (
    <Box
      component="section"
      aria-labelledby={ariaLabelledBy}
      sx={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        mx: { xs: '-12px', md: '-20px' },
        mt: { xs: '-2px', md: '-3px' },
      }}
    >
      <BackgroundVideo />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flex: 1,
          minHeight: 0,
          // The section clips so the backdrop video stays inside its corners,
          // which would otherwise strand anything taller than the viewport
          // with no way to reach it. Scroll the content instead.
          overflowY: 'auto',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
